import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Card,
  message,
  Select,
  Row,
  Col,
  Alert,
  Tabs,
  Tooltip,
  Popconfirm,
  Modal,
  Form,
  Switch,
  Statistic,
  Descriptions,
  Empty,
  Radio,
} from 'antd';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  ApiOutlined,
  CloudDownloadOutlined,
  SyncOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  RedoOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  success: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444',
};

const { Title, Text, Paragraph } = Typography;

const STREAM_STATUS_COLOR: Record<string, string> = {
  active: 'green',
  paused: 'orange',
  error: 'red',
  terminated: 'default',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};

  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

/**
 * antd's Alert keeps its own near-black text colours regardless of the background it
 * is given. Painting one dark without also restyling the text leaves it dark-on-dark
 * and unreadable, so every Alert on this page goes through here.
 */
const DarkAlert = styled(Alert)`
  background: ${COLORS.bgPrimary} !important;
  border: 1px solid ${COLORS.border} !important;

  .ant-alert-message {
    color: ${COLORS.textPrimary} !important;
  }

  .ant-alert-description {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-alert-icon {
    color: ${COLORS.accent} !important;
  }

  &.ant-alert-warning .ant-alert-icon {
    color: ${COLORS.warning} !important;
  }

  &.ant-alert-error .ant-alert-icon {
    color: ${COLORS.danger} !important;
  }

  .ant-alert-close-icon .anticon-close {
    color: ${COLORS.textSecondary} !important;
  }
`;

/** Statistic ships near-black title/value colours that vanish on this theme. */
const statisticStyles = `
  .ant-statistic-title {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-statistic-content,
  .ant-statistic-content-value,
  .ant-statistic-content-prefix,
  .ant-statistic-content-suffix {
    color: ${COLORS.textPrimary};
  }
`;

const PanelCard = styled(Card)`
  margin-bottom: 20px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;

  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    min-height: 48px;
  }

  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
    font-weight: 600;
  }

  .ant-card-body {
    padding: 20px !important;
  }

  ${statisticStyles}

  .ant-descriptions-item-label {
    color: ${COLORS.textSecondary} !important;
    background: ${COLORS.bgPrimary} !important;
  }

  .ant-descriptions-item-content {
    color: ${COLORS.textPrimary} !important;
    word-break: break-all;
  }

  .ant-descriptions-bordered .ant-descriptions-view,
  .ant-descriptions-row {
    border-color: ${COLORS.border} !important;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    border-bottom: 1px solid ${COLORS.border};
  }

  .ant-tabs-tab .ant-tabs-tab-btn {
    color: ${COLORS.textSecondary};
  }

  .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: ${COLORS.textPrimary};
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${COLORS.accent} !important;
  }

  .ant-tabs-ink-bar {
    background: ${COLORS.accent};
  }
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  .ant-pagination {
    .ant-pagination-item {
      background: ${COLORS.bgPrimary};
      border-color: ${COLORS.border};

      a {
        color: ${COLORS.textPrimary};
      }
    }

    .ant-pagination-item-active {
      background: ${COLORS.accentDim};
      border-color: ${COLORS.accent};

      a {
        color: ${COLORS.accent};
      }
    }

    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      background: ${COLORS.bgPrimary};
      border-color: ${COLORS.border};
      color: ${COLORS.textPrimary};
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content,
  .ant-modal-header,
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }

  .ant-modal-header {
    border-bottom: 1px solid ${COLORS.border} !important;

    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }

  .ant-modal-close {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-form-item-label > label {
    color: ${COLORS.textPrimary} !important;
  }

  .ant-form-item-extra {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }

  .ant-input::placeholder {
    color: #64748b !important;
  }

  /* Multi-select tags default to a light background, which hid the light text */
  .ant-select-selection-item {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }

  .ant-select-selection-item-remove,
  .ant-select-arrow {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-select-selection-placeholder {
    color: #64748b !important;
  }

  ${statisticStyles}

  .ant-empty-description {
    color: ${COLORS.textSecondary} !important;
  }

  .ant-form-item-label > label,
  .ant-form-item-extra,
  .ant-typography {
    color: ${COLORS.textPrimary};
  }

  .ant-form-item-extra {
    color: ${COLORS.textSecondary} !important;
  }
`;

const SavedBadge: React.FC<{ saved: boolean; preview?: string | null; savedAt?: string | null }> = ({
  saved,
  preview,
  savedAt,
}) => {
  if (!saved) {
    return <Tag color="red">Not configured</Tag>;
  }
  return (
    <Space size={6} wrap>
      <Tag color="green" icon={<CheckCircleOutlined />}>
        Saved
      </Tag>
      {preview && (
        <Text style={{ color: COLORS.textSecondary, fontFamily: 'monospace', fontSize: 12 }}>
          {preview}
        </Text>
      )}
      {savedAt && (
        <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>
          updated {dayjs(savedAt).fromNow()}
        </Text>
      )}
    </Space>
  );
};

const Moralis: React.FC = () => {
  const [activeTab, setActiveTab] = useState('setup');

  const [config, setConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pullingSecret, setPullingSecret] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingStrict, setSavingStrict] = useState(false);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [secretInput, setSecretInput] = useState('');

  const [streams, setStreams] = useState<any[]>([]);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [streamActionId, setStreamActionId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // EVM and Bitcoin streams live under separate Moralis APIs with different settings.
  const [createKind, setCreateKind] = useState<'evm' | 'bitcoin'>('evm');
  const [bitcoinStreamsError, setBitcoinStreamsError] = useState<string | null>(null);
  const [createForm] = Form.useForm();

  const [addressModalStream, setAddressModalStream] = useState<any>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addingAddresses, setAddingAddresses] = useState(false);

  const [usage, setUsage] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [projectSettings, setProjectSettings] = useState<any>(null);

  const [rescanForm] = Form.useForm();
  const [rescanning, setRescanning] = useState(false);
  const [rescanResult, setRescanResult] = useState<any>(null);

  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [replayingId, setReplayingId] = useState<string | null>(null);

  // ─────────────────────────── config ───────────────────────────

  const fetchConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_MORALIS_CONFIG);
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load Moralis configuration');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) {
      message.warning('Enter an API key first');
      return;
    }
    try {
      setSavingKey(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SAVE_MORALIS_CONFIG, {
        apiKey: apiKeyInput.trim(),
      });
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
        setApiKeyInput('');
        message.success('API key saved');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save API key');
    } finally {
      setSavingKey(false);
    }
  };

  const saveSecret = async () => {
    if (!secretInput.trim()) {
      message.warning('Enter a webhook secret first');
      return;
    }
    try {
      setSavingSecret(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SAVE_MORALIS_CONFIG, {
        webhookSecret: secretInput.trim(),
      });
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
        setSecretInput('');
        message.success('Webhook secret saved');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save webhook secret');
    } finally {
      setSavingSecret(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_TEST_MORALIS_API_KEY);
      message.success(response.data?.message || 'API key is valid');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Moralis rejected the API key');
    } finally {
      setTesting(false);
    }
  };

  const pullSecret = async () => {
    try {
      setPullingSecret(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_PULL_MORALIS_WEBHOOK_SECRET,
      );
      message.success(response.data?.message || 'Webhook secret pulled from Moralis');
      fetchConfig();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to pull webhook secret');
    } finally {
      setPullingSecret(false);
    }
  };

  const toggleAutoProcess = async (enabled: boolean) => {
    try {
      setSavingToggle(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SAVE_MORALIS_CONFIG, {
        autoProcessEnabled: enabled,
      });
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
        message.success(
          enabled
            ? 'Verified deposits will now sweep automatically'
            : 'Automatic sweeping disabled — deposits wait for an admin',
        );
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingToggle(false);
    }
  };

  const toggleStrictSignature = async (enabled: boolean) => {
    try {
      setSavingStrict(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_SAVE_MORALIS_CONFIG, {
        strictSignatureEnabled: enabled,
      });
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
        message.success(
          enabled
            ? 'Unsigned webhooks will now be rejected with 401'
            : 'Unsigned webhooks will be accepted and recorded, but never auto-processed',
        );
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingStrict(false);
    }
  };

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard?.writeText(value).then(
      () => message.success(`${label} copied`),
      () => message.error('Could not copy to clipboard'),
    );
  };

  // ─────────────────────────── streams ───────────────────────────

  const fetchStreams = useCallback(async () => {
    if (!config?.apiKeySet) return;
    try {
      setStreamsLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_MORALIS_STREAMS, {
        params: { limit: 100 },
      });
      if (response.data?.status === 'success') {
        setStreams(response.data.data?.result || []);
        setBitcoinStreamsError(response.data.data?.bitcoinError || null);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load streams');
    } finally {
      setStreamsLoading(false);
    }
  }, [config?.apiKeySet]);

  useEffect(() => {
    if (activeTab === 'streams') fetchStreams();
  }, [activeTab, fetchStreams]);

  /** Primary stream ID for a stream's kind - EVM and Bitcoin each have their own. */
  const primaryIdFor = (stream: any) =>
    stream?.kind === 'bitcoin' ? config?.primaryBitcoinStreamId : config?.primaryStreamId;

  /** Query param telling the API which Moralis product a stream belongs to. */
  const kindParams = (stream: any) => ({ params: { kind: stream?.kind || 'evm' } });

  const changeCreateKind = (kind: 'evm' | 'bitcoin') => {
    setCreateKind(kind);
    const bitcoin = kind === 'bitcoin';
    createForm.setFieldsValue({
      description: bitcoin
        ? 'SwitchWallet Bitcoin deposit monitoring'
        : 'SwitchWallet deposit monitoring',
      tag: bitcoin ? 'switchwallet-bitcoin' : 'switchwallet-deposits',
      // Default to primary only when this kind has no primary yet.
      setAsPrimary: bitcoin ? !config?.primaryBitcoinStreamId : true,
    });
  };

  const openCreateModal = () => {
    setCreateKind('evm');
    createForm.setFieldsValue({
      network: 'mainnet',
      webhookUrl: config?.webhookUrl || '',
      description: 'SwitchWallet deposit monitoring',
      tag: 'switchwallet-deposits',
      chainIds: (config?.supportedChains || []).map((c: any) => c.chainIdHex),
      includeNativeTxs: true,
      includeContractLogs: true,
      includeInternalTxs: false,
      filterPossibleSpamAddresses: false,
      setAsPrimary: true,
    });
    setCreateModalOpen(true);
  };

  const submitCreateStream = async (values: any) => {
    try {
      setCreating(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_MORALIS_STREAM, {
        ...values,
        kind: createKind,
      });
      message.success(response.data?.message || 'Stream created');
      setCreateModalOpen(false);
      createForm.resetFields();
      fetchStreams();
      fetchConfig();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create stream');
    } finally {
      setCreating(false);
    }
  };

  const setStreamStatus = async (stream: any, status: string) => {
    try {
      setStreamActionId(stream.id);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_SET_MORALIS_STREAM_STATUS.replace('{streamId}', stream.id),
        { status },
        kindParams(stream),
      );
      message.success(response.data?.message || `Stream ${status}`);
      fetchStreams();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update stream');
    } finally {
      setStreamActionId(null);
    }
  };

  const deleteStream = async (stream: any) => {
    try {
      setStreamActionId(stream.id);
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_DELETE_MORALIS_STREAM.replace('{streamId}', stream.id),
        kindParams(stream),
      );
      message.success('Stream deleted');
      fetchStreams();
      fetchConfig();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete stream');
    } finally {
      setStreamActionId(null);
    }
  };

  const setAsPrimary = async (stream: any) => {
    try {
      setStreamActionId(stream.id);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_SAVE_MORALIS_CONFIG,
        stream.kind === 'bitcoin'
          ? { primaryBitcoinStreamId: stream.id }
          : { primaryStreamId: stream.id },
      );
      if (response.data?.status === 'success') {
        setConfig(response.data.data);
        message.success('Primary stream updated');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to set primary stream');
    } finally {
      setStreamActionId(null);
    }
  };

  const syncWallets = async (stream: any) => {
    try {
      setStreamActionId(stream.id);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_SYNC_WALLETS_TO_MORALIS_STREAM.replace(
          '{streamId}',
          stream.id,
        ),
        undefined,
        kindParams(stream),
      );
      message.success(response.data?.message || 'Wallets synced');
      fetchStreams();
      if (addressModalStream?.id === stream.id) loadAddresses(stream);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to sync wallets');
    } finally {
      setStreamActionId(null);
    }
  };

  // ─────────────────────────── addresses ───────────────────────────

  const loadAddresses = async (stream: any) => {
    try {
      setAddressesLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_MORALIS_STREAM_ADDRESSES.replace(
          '{streamId}',
          stream.id,
        ),
        { params: { limit: 100, kind: stream?.kind || 'evm' } },
      );
      if (response.data?.status === 'success') {
        setAddresses(response.data.data?.result || []);
        setSyncStatus(response.data.data?.syncStatus || null);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load addresses');
    } finally {
      setAddressesLoading(false);
    }
  };

  const openAddresses = (stream: any) => {
    setAddressModalStream(stream);
    setAddresses([]);
    setSyncStatus(null);
    setAddressInput('');
    loadAddresses(stream);
  };

  const addAddresses = async () => {
    if (!addressInput.trim()) {
      message.warning('Paste one or more addresses first');
      return;
    }
    try {
      setAddingAddresses(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_ADD_MORALIS_STREAM_ADDRESSES.replace(
          '{streamId}',
          addressModalStream.id,
        ),
        { addresses: addressInput },
        kindParams(addressModalStream),
      );
      message.success(response.data?.message || 'Addresses added');
      setAddressInput('');
      loadAddresses(addressModalStream);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to add addresses');
    } finally {
      setAddingAddresses(false);
    }
  };

  const removeAddress = async (address: string) => {
    try {
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_REMOVE_MORALIS_STREAM_ADDRESSES.replace(
          '{streamId}',
          addressModalStream.id,
        ),
        { data: { addresses: [address] }, ...kindParams(addressModalStream) },
      );
      message.success('Address removed');
      loadAddresses(addressModalStream);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to remove address');
    }
  };

  // ─────────────────────────── block rescan ───────────────────────────

  const previewBlock = async () => {
    try {
      const values = await rescanForm.validateFields();
      setRescanning(true);
      setRescanResult(null);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_PREVIEW_MORALIS_BLOCK,
        {
          chainId: values.chainId,
          blockNumber: values.blockNumber,
          addresses: values.addresses || '',
        },
      );
      if (response.data?.status === 'success') {
        setRescanResult(response.data.data);
        message.success(response.data.message || 'Block read');
      }
    } catch (error: any) {
      if (error?.errorFields) return; // form validation, already shown inline
      message.error(error.response?.data?.message || 'Failed to read block');
    } finally {
      setRescanning(false);
    }
  };

  const importBlock = async () => {
    try {
      const values = await rescanForm.validateFields();
      setRescanning(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_INGEST_MORALIS_BLOCK,
        {
          chainId: values.chainId,
          blockNumber: values.blockNumber,
          addresses: values.addresses || '',
        },
      );
      message.success(response.data?.message || 'Block imported');
      // The Incoming Deposits page is where the result actually shows up.
      fetchUsage();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || 'Failed to import block');
    } finally {
      setRescanning(false);
    }
  };

  const resendBlock = async () => {
    try {
      const values = await rescanForm.validateFields();
      setRescanning(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_RESEND_MORALIS_BLOCK,
        { chainId: values.chainId, blockNumber: values.blockNumber },
      );
      message.success(response.data?.message || 'Block sent to the webhook');
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || 'Failed to resend block');
    } finally {
      setRescanning(false);
    }
  };

  // ─────────────────────────── usage & history ───────────────────────────

  const fetchUsage = useCallback(async () => {
    if (!config?.apiKeySet) return;
    try {
      setUsageLoading(true);
      const [usageRes, settingsRes] = await Promise.allSettled([
        apiClient.get(API_ENDPOINTS.ADMIN_GET_MORALIS_USAGE),
        apiClient.get(API_ENDPOINTS.ADMIN_GET_MORALIS_PROJECT_SETTINGS),
      ]);

      if (usageRes.status === 'fulfilled' && usageRes.value.data?.status === 'success') {
        setUsage(usageRes.value.data.data);
      }
      if (
        settingsRes.status === 'fulfilled' &&
        settingsRes.value.data?.status === 'success'
      ) {
        setProjectSettings(settingsRes.value.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load usage');
    } finally {
      setUsageLoading(false);
    }
  }, [config?.apiKeySet]);

  useEffect(() => {
    if (activeTab === 'usage') fetchUsage();
  }, [activeTab, fetchUsage]);

  const fetchHistory = useCallback(async () => {
    if (!config?.apiKeySet) return;
    try {
      setHistoryLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_MORALIS_HISTORY, {
        params: { limit: 50 },
      });
      if (response.data?.status === 'success') {
        setHistory(response.data.data?.result || []);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load delivery history');
    } finally {
      setHistoryLoading(false);
    }
  }, [config?.apiKeySet]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab, fetchHistory]);

  const replayWebhook = async (record: any) => {
    try {
      setReplayingId(record.id);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_REPLAY_MORALIS_HISTORY.replace(
          '{streamId}',
          record.streamId,
        ).replace('{historyId}', record.id),
      );
      message.success(response.data?.message || 'Replay requested');
      fetchHistory();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to replay webhook');
    } finally {
      setReplayingId(null);
    }
  };

  // ─────────────────────────── render ───────────────────────────

  const noKeyNotice = !configLoading && config && !config.apiKeySet && (
    <DarkAlert
      type="warning"
      showIcon
      message="No Moralis API key saved yet"
      description="Save your Moralis API key on the Setup tab. Everything on the other tabs is read from Moralis using that key."
      style={{ marginBottom: 20 }}
    />
  );

  const streamColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string, record: any) => (
        <Space direction="vertical" size={2}>
          <Space size={6}>
            <span>{description || '(no description)'}</span>
            {primaryIdFor(record) === record.id && (
              <Tag color="cyan">
                {record.kind === 'bitcoin' ? 'Primary Bitcoin' : 'Primary'}
              </Tag>
            )}
          </Space>
          {record.tag && (
            <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>
              tag: {record.tag}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => (
        <Tooltip title={record.statusMessage}>
          <Tag color={STREAM_STATUS_COLOR[status] || 'default'}>{status}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Chains',
      dataIndex: 'chainIds',
      key: 'chainIds',
      render: (chainIds: string[], record: any) => (
        <Space size={4} wrap>
          {record.kind === 'bitcoin' &&
            (record.network || ['mainnet']).map((network: string) => (
              <Tag key={network} color="orange">
                Bitcoin {network}
              </Tag>
            ))}
          {record.kind !== 'bitcoin' && (chainIds || []).map((chainId) => {
            const known = (config?.supportedChains || []).find(
              (c: any) => c.chainIdHex?.toLowerCase() === chainId?.toLowerCase(),
            );
            return (
              <Tag key={chainId} color={known ? 'blue' : 'default'}>
                {known ? known.name : chainId}
              </Tag>
            );
          })}
        </Space>
      ),
    },
    {
      title: 'Addresses',
      dataIndex: 'amountOfAddresses',
      key: 'amountOfAddresses',
      render: (count: number) => <span>{count ?? 0}</span>,
    },
    {
      title: 'Events',
      key: 'events',
      render: (_: any, record: any) => (
        <Space size={4} wrap>
          {record.kind === 'bitcoin' && record.includeOutputs !== false && <Tag>outputs</Tag>}
          {record.kind === 'bitcoin' && record.includeInputs && <Tag>inputs</Tag>}
          {record.includeNativeTxs && <Tag>native</Tag>}
          {record.includeContractLogs && <Tag>logs / ERC20</Tag>}
          {record.includeInternalTxs && <Tag>internal</Tag>}
        </Space>
      ),
    },
    {
      title: 'Webhook URL',
      dataIndex: 'webhookUrl',
      key: 'webhookUrl',
      render: (url: string) => {
        const isOurs = config?.webhookUrl && url === config.webhookUrl;
        return (
          <Tooltip title={isOurs ? 'Points at this deployment' : url}>
            <Space size={4}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {url?.length > 40 ? `${url.slice(0, 40)}…` : url}
              </span>
              {isOurs && <Tag color="green">this API</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: string) =>
        updatedAt ? (
          <Tooltip title={dayjs(updatedAt).format('YYYY-MM-DD HH:mm:ss')}>
            <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
              {dayjs(updatedAt).fromNow()}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size={4} wrap>
          <Button
            type="link"
            icon={<WalletOutlined />}
            onClick={() => openAddresses(record)}
            style={{ color: COLORS.accent, padding: 0 }}
          >
            Addresses
          </Button>
          <Popconfirm
            title="Add every member-assigned wallet address to this stream? Newly assigned wallets are registered automatically, so this is only needed to backfill."
            onConfirm={() => syncWallets(record)}
            okText="Sync"
            cancelText="Cancel"
          >
            <Button
              type="link"
              icon={<SyncOutlined />}
              loading={streamActionId === record.id}
              style={{ color: COLORS.accent, padding: 0 }}
            >
              Sync wallets
            </Button>
          </Popconfirm>
          {record.status === 'active' ? (
            <Button
              type="link"
              icon={<PauseCircleOutlined />}
              loading={streamActionId === record.id}
              onClick={() => setStreamStatus(record, 'paused')}
              style={{ color: COLORS.warning, padding: 0 }}
            >
              Pause
            </Button>
          ) : (
            <Button
              type="link"
              icon={<PlayCircleOutlined />}
              loading={streamActionId === record.id}
              onClick={() => setStreamStatus(record, 'active')}
              style={{ color: COLORS.success, padding: 0 }}
            >
              Resume
            </Button>
          )}
          {primaryIdFor(record) !== record.id && (
            <Button
              type="link"
              onClick={() => setAsPrimary(record)}
              loading={streamActionId === record.id}
              style={{ color: COLORS.accent, padding: 0 }}
            >
              Set primary
            </Button>
          )}
          <Popconfirm
            title="Delete this stream on Moralis? Deposits will stop being detected through it."
            onConfirm={() => deleteStream(record)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="link" icon={<DeleteOutlined />} danger style={{ padding: 0 }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'When',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) =>
        date ? (
          <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
            <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
              {dayjs(date).fromNow()}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Stream',
      dataIndex: 'streamId',
      key: 'streamId',
      render: (streamId: string, record: any) => {
        const stream = streams.find((s) => s.id === streamId);
        return (
          <Tooltip title={streamId}>
            <span>{stream?.description || record.tag || streamId?.slice(0, 8)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Webhook URL',
      dataIndex: 'webhookUrl',
      key: 'webhookUrl',
      render: (url: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{url}</span>
      ),
    },
    {
      title: 'Error',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (error: string) => (
        <Text style={{ color: COLORS.danger, fontSize: 12 }}>{error || '-'}</Text>
      ),
    },
    {
      title: 'Records',
      dataIndex: 'tinyPayload',
      key: 'records',
      render: (tinyPayload: any) => <span>{tinyPayload?.records ?? '-'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Popconfirm
          title="Ask Moralis to send this webhook again?"
          onConfirm={() => replayWebhook(record)}
          okText="Replay"
          cancelText="Cancel"
        >
          <Button
            type="link"
            icon={<RedoOutlined />}
            loading={replayingId === record.id}
            style={{ color: COLORS.accent, padding: 0 }}
          >
            Replay
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const setupTab = (
    <>
      <PanelCard title="Moralis API key" loading={configLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <SavedBadge
            saved={!!config?.apiKeySet}
            preview={config?.apiKeyPreview}
            savedAt={config?.apiKeySavedAt}
          />
          {config?.apiKeyFromEnv && (
            <DarkAlert
              type="info"
              showIcon
              message="The key currently in use comes from the MORALIS_API_KEY environment variable. Saving one here overrides it."
            />
          )}
          <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            Used for every call to the Moralis Streams API. Stored server-side and never
            sent back to this page in full — save a new one at any time to rotate it.
          </Paragraph>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={14}>
              <Input.Password autoComplete="new-password"
                placeholder={config?.apiKeySet ? 'Enter a new key to replace the saved one' : 'Paste your Moralis API key'}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                onPressEnter={saveApiKey}
              />
            </Col>
            <Col xs={24} md={10}>
              <Space wrap>
                <Button type="primary" loading={savingKey} onClick={saveApiKey}>
                  {config?.apiKeySet ? 'Replace key' : 'Save key'}
                </Button>
                <Button
                  icon={<ApiOutlined />}
                  loading={testing}
                  onClick={testConnection}
                  disabled={!config?.apiKeySet}
                >
                  Test connection
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </PanelCard>

      <PanelCard title="Webhook secret" loading={configLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <SavedBadge saved={!!config?.webhookSecretSet} preview={config?.webhookSecretPreview} />
          <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            Verifies the <code>x-signature</code> header on every incoming webhook. Until
            this is set, deposits are still recorded but never sweep automatically.
            "Pull from Moralis" reads it straight off your account using the API key above.
          </Paragraph>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={14}>
              <Input.Password autoComplete="new-password"
                placeholder={config?.webhookSecretSet ? 'Enter a new secret to replace it' : 'Paste your Moralis Streams secret'}
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                onPressEnter={saveSecret}
              />
            </Col>
            <Col xs={24} md={10}>
              <Space wrap>
                <Button type="primary" loading={savingSecret} onClick={saveSecret}>
                  {config?.webhookSecretSet ? 'Replace secret' : 'Save secret'}
                </Button>
                <Button
                  icon={<CloudDownloadOutlined />}
                  loading={pullingSecret}
                  onClick={pullSecret}
                  disabled={!config?.apiKeySet}
                >
                  Pull from Moralis
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </PanelCard>

      <PanelCard title="Webhook endpoint" loading={configLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            This is the URL Moralis must post to. It is pre-filled when you create a
            stream from the Streams tab; use it if you create one in the Moralis dashboard
            instead.
          </Paragraph>
          <Space wrap>
            <Text
              code
              style={{ color: COLORS.accent, fontSize: 13, wordBreak: 'break-all' }}
            >
              {config?.webhookUrl || '—'}
            </Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(config?.webhookUrl || '', 'Webhook URL')}
            >
              Copy
            </Button>
          </Space>
        </Space>
      </PanelCard>

      <PanelCard title="Reject unsigned webhooks" loading={configLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space size={12}>
            <Switch
              checked={!!config?.strictSignatureEnabled}
              loading={savingStrict}
              disabled={!config?.webhookSecretSet}
              onChange={toggleStrictSignature}
            />
            <Text style={{ color: COLORS.textPrimary }}>
              {config?.strictSignatureEnabled
                ? 'On — anything without a valid x-signature is refused with 401'
                : 'Off — unsigned deliveries are accepted and recorded, but never auto-processed'}
            </Text>
          </Space>
          {!config?.webhookSecretSet && (
            <DarkAlert
              type="warning"
              showIcon
              message="Save a webhook secret first"
              description="Without a secret there is nothing to verify against, so this cannot be turned on."
            />
          )}
          <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            The endpoint answers 200 to everything by default, which was right for
            rollout but means anyone who learns the URL can post to it. Turning this on
            closes that: only deliveries Moralis actually signed are accepted at all.
            Check the Incoming Deposits list first — every recent record should show
            "Signature verified: Yes" before you switch it on.
          </Paragraph>
        </Space>
      </PanelCard>

      <PanelCard title="Automatic processing" loading={configLoading}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space size={12}>
            <Switch
              checked={!!config?.autoProcessEnabled}
              loading={savingToggle}
              onChange={toggleAutoProcess}
            />
            <Text style={{ color: COLORS.textPrimary }}>
              {config?.autoProcessEnabled
                ? 'On — a verified deposit becomes a sweep and notifies the merchant'
                : 'Off — verified deposits wait for an admin on the Incoming Deposits page'}
            </Text>
          </Space>
          <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
            Turning this off is the safe way to observe Moralis for a while without it
            affecting money movement. Deposits are still recorded and verified on chain
            either way; only the sweep and the merchant webhook are held back.
          </Paragraph>
        </Space>
      </PanelCard>
    </>
  );

  const rescanTab = (
    <PanelCard title="Rescan a block">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
          Moralis only generates a webhook for a block if the address was already on the
          stream when that block was mined. A wallet added later has no history — its
          earlier deposits were never sent and no amount of retrying on our side will
          surface them. Rescanning the block is the only way to recover those.
        </Paragraph>
        <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
          Moralis matches a block against a set of watched addresses, so a scan with no
          addresses reports nothing — the block looks empty when it is not. Leaving the
          field blank matches against every wallet we manage, which is almost always
          what you want.
        </Paragraph>
        <Paragraph style={{ color: COLORS.textSecondary, marginBottom: 0 }}>
          <strong>Feed to incoming deposits</strong> reads the block over Moralis's API and runs it
          through the pipeline here — same wallet matching, same on-chain verification
          against our own RPCs, same sweep and merchant webhook. It does not depend on
          Moralis delivering anything, which is why it is the reliable option.
        </Paragraph>

        <Form form={rescanForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="chainId"
                label="Chain"
                rules={[{ required: true, message: 'Pick a chain' }]}
              >
                <Select
                  placeholder="Select chain"
                  options={(config?.supportedChains || []).map((chain: any) => ({
                    value: chain.chainIdHex,
                    label: `${chain.name} (${chain.chainIdHex})`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="blockNumber"
                label="Block number"
                rules={[{ required: true, message: 'Enter a block number' }]}
              >
                <Input placeholder="e.g. 18614967" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="addresses"
                label="Addresses"
                extra="Optional — defaults to every wallet we manage."
              >
                <Input placeholder="Leave blank to match all our wallets" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Space wrap>
          <Button loading={rescanning} onClick={previewBlock}>
            Preview block
          </Button>
          <Popconfirm
            title="Feed the matching transfers into Incoming Deposits? Each one is verified on chain first, then swept and the merchant notified."
            onConfirm={importBlock}
            okText="Feed"
            cancelText="Cancel"
            disabled={!rescanResult?.summary?.ours}
          >
            <Tooltip
              title={
                !rescanResult
                  ? 'Preview the block first'
                  : !rescanResult.summary?.ours
                    ? 'Nothing in this block lands on a wallet we manage'
                    : undefined
              }
            >
              <Button
                type="primary"
                loading={rescanning}
                disabled={!rescanResult?.summary?.ours}
              >
                Feed to incoming deposits
                {rescanResult?.summary?.ours
                  ? ` (${rescanResult.summary.ours})`
                  : ''}
              </Button>
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title="Ask Moralis to deliver this block to the stream webhook instead? Moralis has not been reliably firing these, so Import block is the dependable route."
            onConfirm={resendBlock}
            okText="Ask Moralis"
            cancelText="Cancel"
          >
            <Button loading={rescanning} disabled={!config?.primaryStreamId}>
              Ask Moralis to redeliver
            </Button>
          </Popconfirm>
        </Space>

        {!config?.primaryStreamId && (
          <DarkAlert
            type="warning"
            showIcon
            message="No primary stream set"
            description="Sending a block to the webhook needs a primary stream — set one on the Streams tab."
          />
        )}

        {rescanResult && (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic
                  title="Transfers found"
                  value={rescanResult.summary?.transfers ?? 0}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Ours"
                  value={rescanResult.summary?.ours ?? 0}
                  valueStyle={{
                    color: rescanResult.summary?.ours ? COLORS.success : COLORS.textSecondary,
                  }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Native txs" value={rescanResult.summary?.txs ?? 0} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Token transfers"
                  value={rescanResult.summary?.erc20Transfers ?? 0}
                  valueStyle={{ color: COLORS.accent }}
                />
              </Col>
            </Row>

            <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>
              Matched against{' '}
              {rescanResult.summary?.usedAllOurWallets
                ? `all ${rescanResult.summary?.addressesUsed ?? 0} wallets we manage`
                : `${rescanResult.summary?.addressesUsed ?? 0} address(es) you supplied`}
              .
            </Text>

            <TableScrollWrapper>
              <Table
                rowKey={(r: any) => `${r.transactionHash}-${r.logIndex ?? 'native'}-${r.toAddress}`}
                size="small"
                pagination={false}
                dataSource={rescanResult.transfers || []}
                style={{ background: 'transparent' }}
                locale={{
                  emptyText: (
                    <span style={{ color: COLORS.textSecondary }}>
                      No transfers in this block
                    </span>
                  ),
                }}
                columns={[
                  {
                    title: 'Ours',
                    dataIndex: 'isOurs',
                    key: 'isOurs',
                    render: (isOurs: boolean, record: any) =>
                      isOurs ? (
                        <Tooltip title={`${record.walletId}${record.memberName ? ` — ${record.memberName}` : ''}`}>
                          <Tag color="green">Ours</Tag>
                        </Tooltip>
                      ) : (
                        <Tag>—</Tag>
                      ),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount: string, record: any) => (
                      <span>
                        {amount} {record.tokenSymbol || ''}
                      </span>
                    ),
                  },
                  {
                    title: 'To',
                    dataIndex: 'toAddress',
                    key: 'toAddress',
                    render: (to: string) => (
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{to}</span>
                    ),
                  },
                  {
                    title: 'Tx',
                    dataIndex: 'transactionHash',
                    key: 'transactionHash',
                    render: (hash: string) => (
                      <Tooltip title={hash}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {hash.slice(0, 10)}…{hash.slice(-6)}
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'transferType',
                    key: 'transferType',
                    render: (type: string) => (
                      <Tag color={type === 'ERC20' ? 'blue' : 'geekblue'}>{type}</Tag>
                    ),
                  },
                ]}
              />
            </TableScrollWrapper>
            <pre
              style={{
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textSecondary,
                padding: 12,
                maxHeight: 420,
                overflow: 'auto',
                fontSize: 12,
                borderRadius: 4,
              }}
            >
              {JSON.stringify(rescanResult.payload, null, 2)}
            </pre>
          </>
        )}
      </Space>
    </PanelCard>
  );

  const usageTab = (
    <>
      <PanelCard title="Moralis account usage" loading={usageLoading}>
        {usage?.moralis ? (
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic
                title="Webhooks delivered"
                value={usage.moralis.totalWebhooksDelivered ?? 0}
                valueStyle={{ color: COLORS.success }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Webhooks failed"
                value={usage.moralis.totalWebhooksFailed ?? 0}
                valueStyle={{
                  color:
                    (usage.moralis.totalWebhooksFailed ?? 0) > 0
                      ? COLORS.danger
                      : COLORS.textPrimary,
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Transactions processed"
                value={usage.moralis.totalTxsProcessed ?? 0}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Logs processed"
                value={usage.moralis.totalLogsProcessed ?? 0}
              />
            </Col>
          </Row>
        ) : (
          <Empty description={<span style={{ color: COLORS.textSecondary }}>No usage data</span>} />
        )}

        <DarkAlert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginTop: 20 }}
          message="About Compute Units"
          description="The Streams API reports webhook and record counts, not Compute Units — CU consumption and plan limits are only visible in the Moralis dashboard. The records above are what you are billed on: roughly 1 record per native transaction and 2 per ERC20 transfer."
        />
      </PanelCard>

      {(usage?.moralis?.streams || []).length > 0 && (
        <PanelCard title="Per-stream usage" loading={usageLoading}>
          <TableScrollWrapper>
            <Table
              rowKey="streamId"
              size="small"
              pagination={false}
              dataSource={usage.moralis.streams}
              style={{ background: 'transparent' }}
              columns={[
                {
                  title: 'Stream',
                  dataIndex: 'streamId',
                  key: 'streamId',
                  render: (streamId: string) => {
                    const stream = streams.find((s) => s.id === streamId);
                    return (
                      <Tooltip title={streamId}>
                        <span>{stream?.description || streamId?.slice(0, 8)}</span>
                      </Tooltip>
                    );
                  },
                },
                { title: 'Delivered', dataIndex: 'totalWebhooksDelivered', key: 'delivered' },
                { title: 'Failed', dataIndex: 'totalWebhooksFailed', key: 'failed' },
                { title: 'Txs', dataIndex: 'totalTxsProcessed', key: 'txs' },
                { title: 'Logs', dataIndex: 'totalLogsProcessed', key: 'logs' },
              ]}
            />
          </TableScrollWrapper>
        </PanelCard>
      )}

      <PanelCard title="What we did with those deposits" loading={usageLoading}>
        {usage?.local ? (
          <Row gutter={[16, 16]}>
            <Col xs={12} md={4}>
              <Statistic title="Received" value={usage.local.received} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Verified on chain"
                value={usage.local.verified}
                valueStyle={{ color: COLORS.accent }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Statistic
                title="Swept"
                value={usage.local.swept}
                valueStyle={{ color: COLORS.success }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Not ours / unsupported"
                value={usage.local.unmatched}
                valueStyle={{ color: COLORS.warning }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Failed"
                value={usage.local.failed}
                valueStyle={{
                  color: usage.local.failed > 0 ? COLORS.danger : COLORS.textPrimary,
                }}
              />
            </Col>
          </Row>
        ) : (
          <Empty description={<span style={{ color: COLORS.textSecondary }}>No deposits yet</span>} />
        )}
      </PanelCard>

      {projectSettings && (
        <PanelCard title="Moralis project settings">
          <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="Delivery region">
              {projectSettings.region || 'Not set'}
            </Descriptions.Item>
            <Descriptions.Item label="Account secret key">
              {projectSettings.secretKeySet ? (
                <Space>
                  <Tag color="green">Present</Tag>
                  <Text style={{ color: COLORS.textSecondary, fontFamily: 'monospace' }}>
                    {projectSettings.secretKeyPreview}
                  </Text>
                </Space>
              ) : (
                <Tag color="red">Not generated</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </PanelCard>
      )}
    </>
  );

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Moralis
        </Title>
        <Space>
          {activeTab === 'streams' && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              disabled={!config?.apiKeySet}
            >
              Create stream
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchConfig();
              if (activeTab === 'streams') fetchStreams();
              if (activeTab === 'usage') fetchUsage();
              if (activeTab === 'history') fetchHistory();
            }}
          >
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      {noKeyNotice}

      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'setup', label: 'Setup', children: setupTab },
          {
            key: 'streams',
            label: 'Streams',
            children: (
              <PanelCard title="Streams on this Moralis account" loading={streamsLoading}>
                {bitcoinStreamsError && (
                  <DarkAlert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Bitcoin streams could not be loaded"
                    description={bitcoinStreamsError}
                  />
                )}
                <TableScrollWrapper>
                  <Table
                    rowKey="id"
                    columns={streamColumns}
                    dataSource={streams}
                    loading={streamsLoading}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    style={{ background: 'transparent' }}
                    locale={{
                      emptyText: (
                        <span style={{ color: COLORS.textSecondary }}>
                          No streams yet — create one to start receiving deposits.
                        </span>
                      ),
                    }}
                  />
                </TableScrollWrapper>
              </PanelCard>
            ),
          },
          { key: 'rescan', label: 'Block rescan', children: rescanTab },
          { key: 'usage', label: 'Usage', children: usageTab },
          {
            key: 'history',
            label: 'Failed deliveries',
            children: (
              <PanelCard title="Webhooks Moralis could not deliver" loading={historyLoading}>
                <Paragraph style={{ color: COLORS.textSecondary }}>
                  Moralis retries a failed delivery for 24 hours and keeps the record for a
                  limited retention window. Anything listed here is a deposit we may never
                  have seen — replay it to have Moralis send it again.
                </Paragraph>
                <TableScrollWrapper>
                  <Table
                    rowKey="id"
                    columns={historyColumns}
                    dataSource={history}
                    loading={historyLoading}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    style={{ background: 'transparent' }}
                    locale={{
                      emptyText: (
                        <span style={{ color: COLORS.success }}>
                          No failed deliveries — every webhook reached us.
                        </span>
                      ),
                    }}
                  />
                </TableScrollWrapper>
              </PanelCard>
            ),
          },
        ]}
      />

      {/* Create stream */}
      <StyledModal
        title={createKind === 'bitcoin' ? 'Create Moralis Bitcoin stream' : 'Create Moralis stream'}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={creating}
        okText="Create stream"
        width={680}
      >
        <Form form={createForm} layout="vertical" onFinish={submitCreateStream}>
          <Form.Item
            label="Stream type"
            extra={
              createKind === 'bitcoin'
                ? 'Watches the Bitcoin address of every member-assigned wallet. Uses the same webhook URL; deposits are verified on chain before sweeping.'
                : 'Watches the EVM address of every member-assigned wallet on the selected chains.'
            }
          >
            <Radio.Group
              value={createKind}
              onChange={(e) => changeCreateKind(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: 'EVM chains', value: 'evm' },
                { label: 'Bitcoin', value: 'bitcoin' },
              ]}
            />
          </Form.Item>
          {createKind === 'bitcoin' && !config?.bitcoinNetwork && (
            <DarkAlert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="No active Bitcoin network"
              description="Bitcoin deposits will be recorded as unsupported until a Bitcoin blockchain network is active."
            />
          )}
          <Form.Item
            name="webhookUrl"
            label="Webhook URL"
            rules={[{ required: true, message: 'Webhook URL is required' }]}
            extra="Where Moralis posts deposits. Pre-filled with this deployment's endpoint."
          >
            <Input placeholder="https://.../Hook/MoralisStreamerRobot" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <Input placeholder="SwitchWallet deposit monitoring" />
          </Form.Item>
          <Form.Item
            name="tag"
            label="Tag"
            rules={
              createKind === 'bitcoin'
                ? [{ required: true, message: 'Moralis requires a tag for Bitcoin streams' }]
                : []
            }
            extra={
              createKind === 'bitcoin'
                ? 'Label returned on every webhook. Required by Moralis for Bitcoin streams.'
                : 'Optional label returned on every webhook.'
            }
          >
            <Input placeholder={createKind === 'bitcoin' ? 'switchwallet-bitcoin' : 'switchwallet-deposits'} />
          </Form.Item>
          {createKind === 'bitcoin' ? (
            <Form.Item name="network" label="Network">
              <Select
                options={[
                  { value: 'mainnet', label: 'Bitcoin mainnet' },
                  { value: 'testnet', label: 'Bitcoin testnet' },
                ]}
              />
            </Form.Item>
          ) : (
          <>
          <Form.Item
            name="chainIds"
            label="Chains"
            rules={[{ required: true, message: 'Select at least one chain' }]}
            extra="Only our active EVM networks are listed — a deposit on any other chain could not be processed anyway."
          >
            <Select
              mode="multiple"
              placeholder="Select chains"
              options={(config?.supportedChains || []).map((chain: any) => ({
                value: chain.chainIdHex,
                label: `${chain.name} (${chain.chainIdHex})`,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="includeNativeTxs"
                label="Native transfers"
                valuePropName="checked"
                extra="ETH, BNB, MATIC deposits."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="includeContractLogs"
                label="Contract logs (ERC20)"
                valuePropName="checked"
                extra="Required for USDT/USDC and any token deposit. Leave on."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="includeInternalTxs"
                label="Internal transactions"
                valuePropName="checked"
                extra="Not ingested by our pipeline yet — costs records with no benefit."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="filterPossibleSpamAddresses"
                label="Filter spam tokens"
                valuePropName="checked"
                extra="Drops contracts Moralis flags as spam before they reach us."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          </>
          )}
          <Form.Item
            name="setAsPrimary"
            label={createKind === 'bitcoin' ? 'Set as primary Bitcoin stream' : 'Set as primary stream'}
            valuePropName="checked"
            extra={
              createKind === 'bitcoin'
                ? 'Newly assigned wallets register their Bitcoin address with the primary Bitcoin stream.'
                : undefined
            }
          >
            <Switch />
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Stream addresses */}
      <StyledModal
        title={`Addresses — ${addressModalStream?.description || ''}`}
        open={!!addressModalStream}
        onCancel={() => setAddressModalStream(null)}
        footer={null}
        width={760}
      >
        {syncStatus && (
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={12} md={6}>
              <Tooltip title="Wallets assigned to a member. Unassigned wallets from the pre-generated pool cannot receive a deposit, so they are not watched.">
                <Statistic
                  title="Assigned wallets"
                  value={syncStatus.eligibleWallets}
                  valueStyle={{ color: COLORS.textPrimary }}
                />
              </Tooltip>
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Synced"
                value={syncStatus.synced}
                valueStyle={{ color: COLORS.success }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Missing"
                value={syncStatus.missing}
                valueStyle={{
                  color: syncStatus.missing > 0 ? COLORS.warning : COLORS.textPrimary,
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Other addresses"
                value={syncStatus.foreignAddressesOnStream}
                valueStyle={{ color: COLORS.textSecondary }}
              />
            </Col>
          </Row>
        )}

        {syncStatus?.missing > 0 && (
          <DarkAlert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${syncStatus.missing} member-assigned wallets are not being watched`}
            description="Deposits to those wallets will not be detected by Moralis until they are added. Wallets assigned from now on are registered automatically."
            action={
              <Button
                size="small"
                type="primary"
                loading={streamActionId === addressModalStream?.id}
                onClick={() => syncWallets(addressModalStream)}
              >
                Sync now
              </Button>
            }
          />
        )}

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input.TextArea
            rows={3}
            placeholder={`Paste one or more ${
              addressModalStream?.kind === 'bitcoin' ? 'Bitcoin' : 'EVM'
            } addresses, separated by commas, spaces or new lines`}
            value={addressInput}
            onChange={(e) => setAddressInput(e.target.value)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={addingAddresses}
            onClick={addAddresses}
          >
            Add addresses
          </Button>

          <TableScrollWrapper>
            <Table
              rowKey="address"
              size="small"
              loading={addressesLoading}
              dataSource={addresses}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              style={{ background: 'transparent' }}
              locale={{
                emptyText: (
                  <span style={{ color: COLORS.textSecondary }}>
                    No addresses on this stream yet
                  </span>
                ),
              }}
              columns={[
                {
                  title: 'Address',
                  dataIndex: 'address',
                  key: 'address',
                  render: (address: string) => (
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{address}</span>
                  ),
                },
                {
                  title: '',
                  key: 'actions',
                  width: 60,
                  render: (_: any, record: any) => (
                    <Popconfirm
                      title="Stop watching this address?"
                      onConfirm={() => removeAddress(record.address)}
                      okText="Remove"
                      cancelText="Cancel"
                    >
                      <Button type="link" icon={<DeleteOutlined />} danger style={{ padding: 0 }} />
                    </Popconfirm>
                  ),
                },
              ]}
            />
          </TableScrollWrapper>
        </Space>
      </StyledModal>
    </PageContainer>
  );
};

export default Moralis;
